import { queryOptions, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";
import { apiUrls } from "../helpers/apiUrls";
import { useAxiosPrivate } from "../hooks/useAxiosPrivate";
import useUserId from "../hooks/useUserId";
import { paginatedResponse } from "../utils";

export function useGetUsers(options = {}, params = new URLSearchParams()) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["users", Object.fromEntries(params.entries())],
        queryFn: () => {
            return usersApi.getUsers(axiosPriv, params);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useGetSellers(options = {}, params = new URLSearchParams()) {
    const axiosPriv = useAxiosPrivate();

    const queryOpts = queryOptions({
        ...options,
        queryKey: ["leadSellers", Object.fromEntries(params.entries())],
        queryFn: () => {
            return usersApi.getSellers(axiosPriv, params);
        },
    });

    return useQuery({
        ...queryOpts,
    });
}

export function useGetSeller(sellerId, options = {}) {
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["leadSeller", sellerId],
        queryFn: () => {
            return usersApi.getSeller(axiosPriv, sellerId);
        },
    });
}

export function useCurrentSeller(options = {}) {
    const userId = useUserId();
    const axiosPriv = useAxiosPrivate();

    return useQuery({
        ...options,
        queryKey: ["currentSeller", userId],
        queryFn: () => {
            if (!userId) {
                return Promise.reject(new Error("No user ID available"));
            }
            return usersApi.getSellerByUserId(axiosPriv, userId);
        },
        enabled: !!userId && options.enabled !== false,
    });
}

export function useDeleteSeller(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: (data) => usersApi.deleteSeller(axiosPriv, data.id),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["leadSellers"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

const PostCreateSellerRequestSchema = z.object({
    user_id: z.string().uuid(),
    seller_scope: z.string(),
});

export function useCreateSeller(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: (data) =>
            usersApi.createSeller(
                axiosPriv,
                usersApiSchema.PostCreateSellerRequestSchema.parse(data),
            ),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["leadSellers"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useCreateEmailFooter(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ sellerId, requestBody }) =>
            usersApi.createEmailFooter(axiosPriv, sellerId, requestBody),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["leadSellers"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useUpdateEmailFooter(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ emailFooterId, requestBody }) =>
            usersApi.updateEmailFooter(axiosPriv, emailFooterId, requestBody),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["leadSellers"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

export function useDeleteEmailFooter(options = {}) {
    const axiosPriv = useAxiosPrivate();
    const queryClient = useQueryClient();

    return useMutation({
        ...options,
        mutationFn: ({ emailFooterId }) => usersApi.deleteEmailFooter(axiosPriv, emailFooterId),
        onSuccess: async (data, variables, context) => {
            await queryClient.invalidateQueries({ queryKey: ["leadSellers"] });
            options.onSuccess?.(data, variables, context);
        },
    });
}

const PostCreateEmailFooterRequestSchema = z.object({
    name: z.string(),
    html_content: z.string(),
    seller: z.string().uuid(),
});
const PutUpdateEmailFooterRequestSchema = z.object({
    name: z.string(),
    html_content: z.string(),
    seller: z.string().uuid(),
});

const EmailFooterSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    html_content: z.string(),
});

const SellerResponseEntrySchema = z.object({
    id: z.string(),
    user_id: z.string(),
    full_name: z.string(),
    username: z.string(),
    scope: z.string().nullable().optional(),
    email_footers: z.array(EmailFooterSchema),
});

const UserResponseEntrySchema = z.object({
    id: z.string().uuid(),
    full_name: z.string(),
});

export const usersApiSchema = {
    seller: SellerResponseEntrySchema,
    GetSellersResponseSchema: z.array(SellerResponseEntrySchema),
    PostCreateSellerRequestSchema: PostCreateSellerRequestSchema,
    GetUsersResponseSchema: paginatedResponse(UserResponseEntrySchema),
    emailFooterSchema: EmailFooterSchema,
    PostCreateEmailFooterRequestSchema: PostCreateEmailFooterRequestSchema,
    PutUpdateEmailFooterRequestSchema: PutUpdateEmailFooterRequestSchema,
};

export const usersApi = {
    async getUsers(axios, params) {
        const response = await axios.get(apiUrls.users.users, { params });
        return usersApiSchema.GetUsersResponseSchema.parse(response.data);
    },

    async getSellers(axios, params) {
        const response = await axios.get(apiUrls.sales.sellers.all, { params });
        return usersApiSchema.GetSellersResponseSchema.parse(response.data);
    },

    async getSeller(axios, sellerId) {
        const response = await axios.get(apiUrls.sales.sellers.detail(sellerId));
        return usersApiSchema.seller.parse(response.data);
    },

    async getSellerByUserId(axios, userId) {
        const response = await axios.get(apiUrls.sales.sellers.byUserId(userId));
        return usersApiSchema.seller.parse(response.data);
    },

    async createSeller(axios, requestBody) {
        const parsedBody = PostCreateSellerRequestSchema.parse(requestBody);
        const response = await axios.post(apiUrls.sales.sellers.create, parsedBody);
        return response.data;
    },

    async deleteSeller(axios, sellerId) {
        await axios.delete(apiUrls.sales.sellers.delete(sellerId));
    },

    async createEmailFooter(axios, sellerId, requestBody) {
        const response = await axios.post(
            apiUrls.sales.sellers.emailFooters.create(sellerId),
            usersApiSchema.PostCreateEmailFooterRequestSchema.parse(requestBody),
        );
        return response.data;
    },

    async deleteEmailFooter(axios, emailFooterId) {
        await axios.delete(apiUrls.sales.sellers.emailFooters.detail(emailFooterId));
    },

    async updateEmailFooter(axios, emailFooterId, requestBody) {
        const response = await axios.put(
            apiUrls.sales.sellers.emailFooters.detail(emailFooterId),
            usersApiSchema.PutUpdateEmailFooterRequestSchema.parse(requestBody),
        );
        return response.data;
    },
};
